// Defence in depth for free text (error messages, stacks, span URLs). Typed fields
// are the primary privacy control; this masks personal data and secrets that slip
// through. Identifiers (UUIDs, Stripe and Twilio IDs) are kept on purpose.
// Keep identical: backend/api/src/observability/scrub.ts and frontend/src/core/telemetry/scrub.ts.
const RULES: ReadonlyArray<readonly [RegExp, string]> = [
  [/\b(?:bearer|basic)\s+[A-Za-z0-9\-._~+/]+=*/gi, "[credential]"],
  [/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, "[jwt]"],
  [/\b(?:sk|rk|whsec)_(?:test_|live_)?[A-Za-z0-9]{6,}/g, "[secret]"],
  [/\b([A-Za-z]{2,6}_[A-Za-z0-9]+)_secret_[A-Za-z0-9]+/g, "$1_secret_[secret]"],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]"],
  [/\b\d(?:[ -]?\d){12,18}\b/g, "[card]"],
  [/(?:\+|\b)\d(?:[ ().-]{0,2}\d){8,14}\b/g, "[phone]"],
];

// Digit runs inside UUIDs look like phone or card numbers, so UUIDs are never scrubbed.
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

export function scrub(text: string): string {
  let result = "";
  let last = 0;
  for (const match of text.matchAll(UUID_PATTERN)) {
    result += scrubPart(text.slice(last, match.index)) + match[0];
    last = match.index + match[0].length;
  }
  return result + scrubPart(text.slice(last));
}

function scrubPart(text: string): string {
  return RULES.reduce((value, [pattern, replacement]) => value.replace(pattern, replacement), text);
}

const ID_SEGMENT =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+|[A-Za-z]{2,6}_[A-Za-z0-9_]{6,}|[A-Z]{2}[0-9a-f]{32})$/i;

/** Drops the query string and fragment, and replaces identifier-like path segments with `:id`. */
export function redactUrl(url: string): string {
  const withoutQuery = url.split(/[?#]/, 1)[0] ?? "";
  const origin = /^[a-z][a-z0-9+.-]*:\/\/[^/]*/i.exec(withoutQuery)?.[0] ?? "";
  const path = withoutQuery.slice(origin.length)
    .split("/")
    .map(segment => {
      const decoded = decodeSegment(segment);
      if (ID_SEGMENT.test(decoded)) return ":id";
      const scrubbed = scrub(decoded);
      return scrubbed === decoded ? segment : scrubbed;
    })
    .join("/");
  return origin + path;
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
